import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "Components/DesignSystem/Toast/useToast";

import { AppContext } from "Context/AppContext";
import ModalContainer from "Components/Common/Modal/ModalContainer";
import ModalHeader from "Components/Common/Modal/ModalHeader";
import ModalBody from "Components/Common/Modal/ModalBody";
import ModalFooter from "Components/Common/Modal/ModalFooter";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import FieldTitle from "Components/DesignSystem/FieldTitle/FieldTitle";
import { Add, Cross, Delete } from "Components/DesignSystem/Iconography/Icons";
import FillButton from "Components/Common/components/Button/FillButton";
import BorderedButton from "Components/Common/components/Button/BorderedButton";
import {
  fetchEnvironments,
  fetchParentChannels,
  EnvironmentOption,
  ParentChannelOption,
} from "Components/Admin/connections/Services/syncEnvironmentsService";
import {
  syncSkusToEnvironments,
  TDiagnosticsProduct,
  TSkuSyncItem,
} from "api/Diagnostics";

/**
 * "Sync Selected Products" modal (US 12396 / task 13324).
 *
 * Workspace=party, Domain=industry, Channel=parent channel, Environment=child
 * channel. Party and domain come from AppContext; the parent channels and their
 * environments are not held in context, so they are fetched here — the same
 * source the Connections "Sync to Environments" config uses.
 */

const ALL_VALUE = "ALL";

type TargetRow = {
  id: number;
  /** parent channel id, never ALL_VALUE — the Channel dropdown has no All option */
  channel: string;
  /** child channel_key, or ALL_VALUE for every env under the channel */
  env: string;
};

let nextRowId = 0;
const emptyRow = (): TargetRow => ({ id: ++nextRowId, channel: "", env: "" });

type Taken = { fullChannels: Set<string>; envs: Record<string, Set<string>> };

interface SyncProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** the checked products from the diagnostics table */
  products: TDiagnosticsProduct[];
  /** called after a successful sync so the table can reload */
  onSynced: () => void;
}

const SyncProductsModal = ({
  isOpen,
  onClose,
  products,
  onSynced,
}: SyncProductsModalProps) => {
  const appData = useContext(AppContext);
  const partyId: string = appData?.selectedParty?.id ?? "";
  const domainId: string = appData?.selectedIndustry?.id ?? "";
  const currentChannelKey: string = appData?.channelKey ?? "";

  const [parentChannels, setParentChannels] = useState<ParentChannelOption[]>([]);
  const [envByChannel, setEnvByChannel] = useState<Record<string, EnvironmentOption[]>>({});
  const [sourceEnv, setSourceEnv] = useState<string>("");
  const [rows, setRows] = useState<TargetRow[]>([emptyRow()]);
  const [syncing, setSyncing] = useState<boolean>(false);
  const requestedEnvsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen || !partyId || !domainId) return;
    fetchParentChannels(partyId, domainId).then((res) => {
      if (res.success && res.data) setParentChannels(res.data);
      else toast.error(res.error ?? "Failed to load channels for this domain");
    });
  }, [isOpen, partyId, domainId]);

  // Environments are fetched per channel as soon as the channels are known, so
  // the source list and a per-channel "All" can both be resolved locally.
  // The ref keeps each channel to one request: keying off envByChannel instead
  // would re-fire for every channel still missing each time one resolves.
  useEffect(() => {
    if (!partyId || parentChannels.length === 0) return;
    parentChannels.forEach((channel) => {
      const requestKey = `${partyId}:${channel.id}`;
      if (requestedEnvsRef.current.has(requestKey)) return;
      requestedEnvsRef.current.add(requestKey);
      fetchEnvironments(partyId, channel.id).then((res) => {
        // Resolve to [] on failure too — the source pre-fill below waits for
        // every channel to report in, so a failure must not stall it forever.
        setEnvByChannel((prev) => ({
          ...prev,
          [channel.id]: res.success && res.data ? res.data : [],
        }));
      });
    });
  }, [partyId, parentChannels]);

  const envsLoaded =
    parentChannels.length > 0 &&
    parentChannels.every((channel) => envByChannel[channel.id] !== undefined);

  // Every channel-environment combination in the domain, current one first.
  const sourceOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [];
    parentChannels.forEach((channel) => {
      (envByChannel[channel.id] || []).forEach((env) => {
        const label = `${channel.name} - ${env.name}`;
        options.push({
          label: env.key === currentChannelKey ? `${label} (current)` : label,
          value: env.key,
        });
      });
    });
    return options.sort(
      (a, b) => Number(b.value === currentChannelKey) - Number(a.value === currentChannelKey)
    );
  }, [parentChannels, envByChannel, currentChannelKey]);

  // Pre-fill with the current environment once EVERY channel's environments have
  // loaded. The per-channel fetches resolve one at a time, so defaulting from a
  // partial list would lock in whichever env arrived first — the current one is
  // usually still in flight, and the `sourceEnv` guard makes that permanent.
  useEffect(() => {
    if (sourceEnv || !envsLoaded || sourceOptions.length === 0) return;
    const current = sourceOptions.find((opt) => opt.value === currentChannelKey);
    setSourceEnv(current?.value ?? sourceOptions[0].value);
  }, [envsLoaded, sourceOptions, sourceEnv, currentChannelKey]);

  // The Channel dropdown has no "All" option (US 12396); Environment does.
  const channelOptions = useMemo(
    () => parentChannels.map((channel) => ({ label: channel.name, value: channel.id })),
    [parentChannels]
  );

  // The source environment is never a target of itself.
  const targetEnvsOf = (channelId: string) =>
    (envByChannel[channelId] || []).filter((env) => env.key !== sourceEnv);

  const takenByOtherRows = (rowId?: number): Taken => {
    const taken: Taken = { fullChannels: new Set(), envs: {} };
    rows.forEach((row) => {
      if (row.id === rowId || !row.channel) return;
      if (row.env === ALL_VALUE) {
        taken.fullChannels.add(row.channel);
      } else if (row.env) {
        (taken.envs[row.channel] = taken.envs[row.channel] || new Set()).add(row.env);
      }
    });
    return taken;
  };

  const channelHasRoom = (channelId: string, taken: Taken) => {
    if (taken.fullChannels.has(channelId)) return false;
    if (envByChannel[channelId] === undefined) return true;
    return targetEnvsOf(channelId).some((env) => !taken.envs[channelId]?.has(env.key));
  };

  const getChannelOptions = (row: TargetRow) => {
    const taken = takenByOtherRows(row.id);
    return channelOptions.filter(
      (option) => option.value === row.channel || channelHasRoom(option.value, taken)
    );
  };

  // Environments another row already picked are not offered again, and "All"
  // is offered only while no other row holds a specific env of that channel.
  const getEnvOptions = (row: TargetRow) => {
    if (!row.channel) return [];
    const taken = takenByOtherRows(row.id);
    if (taken.fullChannels.has(row.channel)) return [];
    const pickedElsewhere = taken.envs[row.channel];
    const envs = targetEnvsOf(row.channel).filter(
      (env) => env.key === row.env || !pickedElsewhere?.has(env.key)
    );
    const allOption =
      pickedElsewhere?.size || envs.length === 0 ? [] : [{ label: "All", value: ALL_VALUE }];
    return [...allOption, ...envs.map((env) => ({ label: env.name, value: env.key }))];
  };

  const canAddRow = (() => {
    const taken = takenByOtherRows();
    return parentChannels.some((channel) => channelHasRoom(channel.id, taken));
  })();

  // Expand rows into concrete channel_keys. A per-channel "All" fans out to every
  // env under that channel; the source env is never a target of itself.
  const targetEnvs = useMemo(() => {
    const keys = new Set<string>();
    rows.forEach((row) => {
      if (!row.channel || !row.env) return;
      if (row.env === ALL_VALUE) {
        (envByChannel[row.channel] || []).forEach((env) => keys.add(env.key));
      } else {
        keys.add(row.env);
      }
    });
    keys.delete(sourceEnv);
    return Array.from(keys);
  }, [rows, envByChannel, sourceEnv]);

  // "Products already present in a target environment will be skipped
  // automatically" — the backend upserts, so the skip happens here.
  const payload: TSkuSyncItem[] = useMemo(
    () =>
      products
        .map((product) => ({
          gms_id: product.gms_id,
          sku: product.sku ?? "",
          target_envs: targetEnvs.filter(
            (env) => !(product.synced_envs ?? []).includes(env)
          ),
        }))
        .filter((item) => item.target_envs.length > 0),
    [products, targetEnvs]
  );

  const handleSourceChange = (value: string) => {
    setSourceEnv(value);
    setRows((prev) => prev.map((row) => (row.env === value ? { ...row, env: "" } : row)));
  };

  const handleChannelChange = (rowId: number, value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, channel: value, env: "" } : row))
    );
  };

  const handleEnvChange = (rowId: number, value: string) => {
    setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, env: value } : row)));
  };

  const addRow = () => {
    if (canAddRow) setRows((prev) => [...prev, emptyRow()]);
  };

  const removeRow = (rowId: number) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== rowId) : prev));

  const handleSyncNow = async () => {
    if (!targetEnvs.length) {
      toast.warning("Select a target channel and environment to sync the products to.");
      return;
    }
    if (rows.some((row) => row.channel && !row.env)) {
      toast.warning("Select an environment for every chosen channel before syncing.");
      return;
    }
    if (!payload.length) {
      toast.info(
        "All selected products are already mapped in the chosen environments."
      );
      return;
    }
    setSyncing(true);
    try {
      await syncSkusToEnvironments(payload);
      const envCount = new Set(payload.flatMap((item) => item.target_envs)).size;
      toast.success(
        `SKU mappings created for ${payload.length} products in ${envCount} environments.`
      );
      onSynced();
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to sync the selected products");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <ModalContainer
      id="diagnostics-sync-modal"
      isOpen={isOpen}
      setOpen={onClose}
      width="438px"
    >
      <ModalHeader>
        <div className="tw-flex tw-items-center tw-justify-between">
          <span className="tw-font-semibold tw-text-ink">
            Sync Selected Products
          </span>
          <button
            id="diagnostics-sync-modal-close"
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-text-ink-secondary tw-cursor-pointer"
          >
            <Cross size={12} />
          </button>
        </div>
      </ModalHeader>

      <ModalBody>
        <p className="tw-mb-3 tw-text-sm tw-text-ink-secondary">
          Choose source and target environments. Products already present in a
          target environment will be skipped automatically.
        </p>

        <p className="tw-mb-4 tw-text-sm tw-text-ink">
          <strong>{products.length}</strong> Product/s Selected
        </p>

        <div className="tw-mb-4">
          <Dropdown
            label="Source environment (single select)"
            className="tw-w-full"
            placeholder="Select"
            usePortal
            options={sourceOptions}
            value={sourceEnv}
            onChange={(value) => handleSourceChange(String(value))}
            data-testid="diagnostics-sync-modal-source"
          />
        </div>

        <FieldTitle label="Target environments" className="tw-mb-2" />

        {rows.map((row, index) => (
          <div
            key={row.id}
            className="tw-flex tw-items-end tw-gap-4 tw-mb-3"
            data-testid={`diagnostics-sync-modal-row-${row.id}`}
          >
            <Dropdown
              label={index === 0 ? "Channel" : ""}
              className="tw-min-w-0 tw-flex-1 tw-basis-0"
              placeholder="Select"
              usePortal
              options={getChannelOptions(row)}
              value={row.channel}
              onChange={(value) => handleChannelChange(row.id, String(value))}
              data-testid={`diagnostics-sync-modal-row-${row.id}-channel`}
            />
            <Dropdown
              label={index === 0 ? "Environment" : ""}
              className="tw-min-w-0 tw-flex-1 tw-basis-0"
              placeholder="Select"
              usePortal
              disabled={!row.channel}
              options={getEnvOptions(row)}
              value={row.env}
              onChange={(value) => handleEnvChange(row.id, String(value))}
              data-testid={`diagnostics-sync-modal-row-${row.id}-env`}
            />
            <button
              type="button"
              aria-label="Remove target environment"
              onClick={() => removeRow(row.id)}
              data-testid={`diagnostics-sync-modal-row-${row.id}-remove`}
              disabled={rows.length === 1}
              className={`tw-mb-2 tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent ${
                rows.length === 1
                  ? "tw-text-ink-disabled tw-cursor-not-allowed"
                  : "tw-text-ink-secondary hover:tw-text-tone-danger-glyph tw-cursor-pointer"
              }`}
            >
              <Delete size={20} />
            </button>
          </div>
        ))}

        <button
          id="diagnostics-sync-modal-add-row"
          data-testid="diagnostics-sync-modal-add-row"
          type="button"
          onClick={addRow}
          disabled={!canAddRow}
          className={`tw-flex tw-items-center tw-gap-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium ${
            canAddRow ? "tw-text-accent tw-cursor-pointer" : "tw-text-ink-disabled tw-cursor-not-allowed"
          }`}
        >
          <Add size={20} />
          Add more channel
        </button>
      </ModalBody>

      <ModalFooter>
        <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-space-x-3">
          <BorderedButton id="diagnostics-sync-modal-cancel" onClick={onClose}>
            Cancel
          </BorderedButton>
          <FillButton
            id="diagnostics-sync-modal-confirm"
            data-testid="diagnostics-sync-modal-confirm"
            onClick={handleSyncNow}
            disabled={syncing}
          >
            {syncing ? "Syncing…" : "Sync Now"}
          </FillButton>
        </div>
      </ModalFooter>
    </ModalContainer>
  );
};

export default SyncProductsModal;
